import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useInfiniteQuery } from 'react-query'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import { VaultCardGrouped } from 'pages/vault/components/VaultCardGrouped'
import { getSharedVaultMedia } from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'

import style from './_shared_media_modal.module.scss'
import { SharedMediaFilterTabs } from './SharedMediaFilterTabs'

enum SharedMediaDirection {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing'
}

export type SharedVaultFilter =
  | 'all'
  | 'paid'
  | 'unpaid'

const SharedMediaModalChat: FC<{
  submitFn?: (val: any) => void
  user?: any
  selected?: any
  userMedia?: any
  checkingData?: any
  setPurchaseMsgId?: any
  setConfirmBuyModalOpen?: any
  setSelectedMessageBuyPrice?: any
  scrollToMessage?: any
  setMessageId?: (id: string | null) => void
  sharedWith?: string
}> = ({
  submitFn,
  user,
  userMedia,
  checkingData,
  setPurchaseMsgId,
  setConfirmBuyModalOpen,
  setSelectedMessageBuyPrice,
  scrollToMessage,
  setMessageId,
  sharedWith
}) => {
  const modalData = useModalContext()
  const profileCache = useProfileCacheStore((state) => state.getProfile('self'))
  const setProfile = useProfileCacheStore((state) => state.setProfile)
  const fallbackUser = useUserProfile()
  const userData = profileCache ?? fallbackUser

  const [allSelectedMedia, setAllSelectedMedia] = useState<any[]>([])
  const [selectedPosts, setSelecetedPosts] = useState<Array<string | number>>([])
  const [allMediaLength, setAllMediaLength] = useState<number>(0)

  const [filter, setFilter] =
    useState<SharedVaultFilter>('all')

  const { t } = useTranslation()
  const type = 'messages'
  const grouped = true

  const [direction, setDirection] = useState<SharedMediaDirection>(
    userData?.role === 'model'
      ? SharedMediaDirection.OUTGOING
      : SharedMediaDirection.INCOMING
  )

  const showSelection =
  direction === SharedMediaDirection.OUTGOING

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    [
      'sharedVaultMedia',
      user?.id,
      direction,
      filter
    ],
    async ({ pageParam = 1 }) => {
      if (!user?.id) {
        throw new Error('Missing shared vault target user')
      }

      return await getSharedVaultMedia(
        user.id,
        pageParam,
        {
          direction,
          filter
        }
      )
    },
    {
      enabled: Boolean(user?.id),
      getNextPageParam: (lastPage) => lastPage.nextPage
    }
  )

  const media = useMemo(
    () =>
      data?.pages.flatMap(
        (page: any) => page?.data ?? []
      ) ?? [],
    [data]
  )

  
  useEffect(() => {
    const cached = profileCache

    if (!cached && fallbackUser?.username) {
      setProfile('self', fallbackUser)
    }
  }, [
    profileCache,
    fallbackUser,
    setProfile
  ])

  const observer = useRef<IntersectionObserver>()

  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return

      if (observer.current) {
        observer.current.disconnect()
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0]?.isIntersecting &&
            hasNextPage
          ) {
            fetchNextPage()
          }
        },
        {
          rootMargin: '150px'
        }
      )

      if (node) {
        observer.current.observe(node)
      }
    },
    [
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage
    ]
  )

  const getSelectionKey = (
    val: any
  ): string | undefined => {
    return val?.message?.message?.id
  }

  const computeCalcNumber = (
    val: any
  ): number => {
    const message = val?.message?.message

    if (!message) {
      return 0
    }

    return (
      (message.photos?.length ?? 0) +
      (message.videos?.length ?? 0) +
      (message.sounds?.length ?? 0)
    )
  }

  const toggleSelectedPosts = (
    val: any
  ) => {
    const calcNumber = computeCalcNumber(val)
    const selectionId = getSelectionKey(val)

    if (!selectionId) {
      console.warn(
        'SharedMediaModalChat: could not resolve selection key for',
        val
      )

      return
    }

    const alreadySelected =
      selectedPosts.includes(selectionId)

    if (alreadySelected) {
      setSelecetedPosts((prev) =>
        prev.filter(
          (id) => id !== selectionId
        )
      )

      setAllSelectedMedia((prev) =>
        prev.filter(
          (item) =>
            getSelectionKey(item) !==
            selectionId
        )
      )

      setAllMediaLength((prev) =>
        Math.max(
          0,
          prev - calcNumber
        )
      )

      return
    }

    if (
      allMediaLength + calcNumber >
      40
    ) {
      addToast(
        'error',
        'Only 40 media files can be selected at once'
      )

      return
    }

    setSelecetedPosts((prev) => [
      ...prev,
      selectionId
    ])

    setAllSelectedMedia((prev) => [
      ...prev,
      val
    ])

    setAllMediaLength((prev) =>
      prev + calcNumber
    )
  }

  const submitMedia = async (): Promise<void> => {
    if (!submitFn) return

    if (setMessageId) {
      const ids = (allSelectedMedia ?? [])
        .map(
          (item: any) =>
            item?.message?.previuos_message_ids
        )
        .flat()
        .filter(Boolean)

      setMessageId(
        Array.isArray(ids) && ids.length
          ? ids[0]
          : null
      )
    }

    submitFn(allSelectedMedia)
    modalData.clearModal()
  }

  useEffect(() => {
    setAllSelectedMedia([])
    setSelecetedPosts([])
    setAllMediaLength(0)
  }, [
    direction,
    filter
  ])

  return (
    <div className={style.container}>
      <div className={style.title}>
        {user
          ? t('Shared Media')
          : t('Shared Message')}
      </div>

      <div className={style.title_container}>
        {userData?.role === 'model' && !user && (
          <div className={style.withHeaderSectionView}>
            <div />
            <div className={style.sort_buttons_container} />
          </div>
        )}
      </div>

      {user ? (
        <>
          {userData?.role === 'model' ? (
            <div className={style.shared_with_container}>
              <div className={style.shared_with_text}>
                {t('sharedWith')}
              </div>

              <div className={style.user_details}>
                <div className={style.user_avatar}>
                  <img
                    src={
                      user?.avatar?.url ||
                      avatarPlaceholder
                    }
                    alt="avatar"
                  />
                </div>

                <div className={style.user_name}>
                  <div>
                    {user?.display_name || ''}
                  </div>

                  <div>
                    @{user?.username || ''}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={style.links_container}>
              <button
                className={`${style.link_button} ${
                  filter === 'all'
                    ? style.link_button_active
                    : ''
                }`}
                onClick={() =>
                  setFilter('all')
                }
              >
                All
              </button>

              <button
                className={`${style.link_button} ${
                  filter === 'paid'
                    ? style.link_button_active
                    : ''
                }`}
                onClick={() =>
                  setFilter('paid')
                }
              >
                Paid
              </button>

              <button
                className={`${style.link_button} ${
                  filter === 'unpaid'
                    ? style.link_button_active
                    : ''
                }`}
                onClick={() =>
                  setFilter('unpaid')
                }
              >
                Unpaid
              </button>
            </div>
          )}
        </>
      ) : null}

      {sharedWith && (
        <SharedMediaFilterTabs
          direction={direction}
          setDirection={setDirection}
        />
      )}

      <div className={style.cards_container}>
        {isLoading ? (
          <div className="profilefeed skeleton">
            <Skeleton
              height={320}
              style={{
                marginTop: 30
              }}
            />
          </div>
        ) : (
          media.map(
            (
              m: any,
              index: number
            ) => {
              const selectionId =
                getSelectionKey(m)

              const isSelected =
                allSelectedMedia.some(
                  (item: any) =>
                    getSelectionKey(item) ===
                    selectionId
                )

              const selectedIndex =
                selectedPosts.indexOf(
                  selectionId ?? ''
                ) + 1

              return (
<VaultCardGrouped
  origin="shared-media-modal-chat"
  showSelection={showSelection}
  onCirlceClick={toggleSelectedPosts}
  media={m}
  selected={isSelected}
  selectedText={selectedIndex}
  grouped={grouped}
  key={selectionId ?? index}
  customClass={style.mobileView}
  customCirlce={style.customCirlce}
  userMedia={userMedia}
  checkingData={checkingData}
  type={type}
  setPurchaseMsgId={setPurchaseMsgId}
  setConfirmBuyModalOpen={setConfirmBuyModalOpen}
  setSelectedMessageBuyPrice={setSelectedMessageBuyPrice}
  scrollToMessage={scrollToMessage}
/>
              )
            }
          )
        )}

        {(hasNextPage ?? isLoading) && (
          <div
            style={{
              width: '100%'
            }}
            ref={lastPostRef}
          >
            <div className="loader" />
          </div>
        )}
      </div>

      <div className={style.buttons_container}>
        <Button
          color="grey"
          text={t('close')}
          clickFn={() => {
            modalData.clearModal()
          }}
        />

        {direction === SharedMediaDirection.OUTGOING && (
          <Button
            color="black"
            text={t('addMedia')}
            clickFn={submitMedia}
          />
        )}
      </div>
    </div>
  )
}

export default SharedMediaModalChat